import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRace } from '@/hooks/useRaces';
import { useRaceTypes } from '@/hooks/useRaceTypes';
import { useRaceRegistrations } from '@/hooks/useRegistrations';
import RaceContestantsView from './racecontestantspage.view';

const RaceContestantsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: raceData, isLoading: raceLoading } = useRace(id);
  const { data: raceTypesData = [], isLoading: typesLoading } = useRaceTypes(id);
  const { data: registrationsData = [], isLoading: regsLoading } = useRaceRegistrations(id);

  const loading = raceLoading || typesLoading || regsLoading;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterRaceType, setFilterRaceType] = useState('');
  const [sortBy, setSortBy] = useState('bibNumber');

  const event = useMemo(() => {
    if (!raceData) return null;
    return { id: raceData.id, title: raceData.name };
  }, [raceData]);

  const eventRaceTypes = useMemo(() => raceTypesData.map(rt => ({
    id: rt.id,
    name: rt.name,
    distance: rt.distance,
    maxParticipants: rt.maxParticipants
  })), [raceTypesData]);

  const eventRegistrations = useMemo(() => registrationsData
    .filter(r => r.registrationStatus === 'Confirmed')
    .map(r => ({
      id: r.id,
      firstName: r.firstName,
      lastName: r.lastName,
      city: r.city,
      country: r.country,
      bibNumber: r.bibNumber || 0,
      raceTypeId: r.raceTypeId,
      teamName: r.teamName || ''
    })), [registrationsData]);

  const getRaceTypeName = (raceTypeId) => {
    const raceType = eventRaceTypes.find((rt) => rt.id === raceTypeId);
    return raceType ? raceType.name : 'N/A';
  };

  const contestantColumns = useMemo(() => [
    { key: 'bibNumber', label: 'Број', cellClassName: 'whitespace-nowrap', render: (v) => <span className="font-semibold text-lg text-blue-600">#{v}</span> },
    { key: 'firstName', label: 'Име', cellClassName: 'whitespace-nowrap', render: (_, reg) => (
      <div>
        <div className="font-medium text-gray-900">{reg.firstName} {reg.lastName}</div>
        {reg.teamName && <div className="text-sm text-gray-500">Тим: {reg.teamName}</div>}
      </div>
    )},
    { key: 'city', label: 'Град', cellClassName: 'whitespace-nowrap text-gray-600' },
    { key: 'country', label: 'Држава', cellClassName: 'whitespace-nowrap text-gray-600' },
    { key: 'raceTypeId', label: 'Тип на трка', cellClassName: 'whitespace-nowrap', render: (v) => (
      <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">{getRaceTypeName(v)}</span>
    )},
  ], [eventRaceTypes]);

  const filteredRegistrations = eventRegistrations
    .filter((reg) => {
      const matchesSearch =
        reg.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        reg.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        reg.bibNumber.toString().includes(searchQuery);
      const matchesRaceType = !filterRaceType || reg.raceTypeId === filterRaceType;
      return matchesSearch && matchesRaceType;
    })
    .sort((a, b) => {
      if (sortBy === 'bibNumber') {
        return a.bibNumber - b.bibNumber;
      } else {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      }
    });

  return (
    <RaceContestantsView
      event={event}
      eventRaceTypes={eventRaceTypes}
      eventRegistrations={eventRegistrations}
      filteredRegistrations={filteredRegistrations}
      loading={loading}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filterRaceType={filterRaceType}
      setFilterRaceType={setFilterRaceType}
      sortBy={sortBy}
      setSortBy={setSortBy}
      contestantColumns={contestantColumns}
      navigate={navigate}
    />
  );
};

export default RaceContestantsPage;
